import React from 'react';
import { Coins } from 'lucide-react';
import { Card } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useTokenBalance } from '@/hooks/useTokenBalance';
import { toDate } from '@/lib/date-utils';
import { useUserTimezone } from '@/hooks/useDateFormat';

/**
 * Token balance widget — "tokens remaining this period". Each token covers up
 * to 5 minutes of test time. Unlimited plans render nothing.
 */
export function ExecutionQuotaWidget() {
  const { activeOrganization } = useAuth();
  const { balance, grant, used, unlimited, percentUsed, loading } = useTokenBalance();
  const tz = useUserTimezone();

  // Don't render for unlimited plans
  if (unlimited) return null;

  const billingPeriodEnd = activeOrganization?.subscription?.billingPeriodEnd;
  const endDate = billingPeriodEnd ? toDate(billingPeriodEnd) : null;
  const endLabel = endDate
    ? endDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: tz })
    : null;

  // Higher consumption = warmer color (uses % of allowance spent).
  const progressColor =
    percentUsed == null ? 'bg-accent-blue' :
    percentUsed >= 90   ? 'bg-status-error' :
    percentUsed >= 75   ? 'bg-status-pending' :
                          'bg-status-success';

  return (
    <Card padding="lg" className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Coins className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium text-muted-foreground">Tokens this period</span>
      </div>

      <div className="flex items-end justify-between gap-2">
        <div className="text-2xl font-semibold text-foreground leading-none">
          {loading ? '—' : balance != null ? balance.toLocaleString() : '—'}
        </div>
        {!loading && grant != null && (
          <span className="text-sm text-muted-foreground mb-0.5">
            of {grant.toLocaleString()}
          </span>
        )}
      </div>

      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${progressColor}`}
          style={{ width: `${percentUsed ?? 0}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {loading ? '—' : used != null ? `${used.toLocaleString()} used · 1 token = 1 test (up to 5 min)` : '1 token = 1 test (up to 5 min)'}
        </span>
        {endLabel && <span>Resets {endLabel}</span>}
      </div>
      {!loading && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          Each test uses at least 1 token. A token covers 5 minutes of run time —
          a longer test uses one more token per additional 5 minutes.
        </p>
      )}
    </Card>
  );
}
